import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BanknotesIcon,
  ChartBarIcon,
  ClockIcon,
  FireIcon,
  LockClosedIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  GiftIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

interface StakingPool {
  id: string;
  name: string;
  description: string;
  apy: number;
  minStake: number;
  lockPeriod: number;
  totalStaked: number;
  yourStake: number;
  rewards: number;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  features: string[];
}

const stakingPools: StakingPool[] = [
  {
    id: 'bronze',
    name: 'Bronze Pool',
    description: 'Entry-level staking with flexible terms',
    apy: 15,
    minStake: 100,
    lockPeriod: 0,
    totalStaked: 2500000,
    yourStake: 0,
    rewards: 0,
    tier: 'bronze',
    features: ['No lock period', '1% fee discount', 'Basic governance'],
  },
  {
    id: 'silver',
    name: 'Silver Pool',
    description: 'Enhanced rewards with 30-day commitment',
    apy: 20,
    minStake: 1000,
    lockPeriod: 30,
    totalStaked: 5000000,
    yourStake: 1500,
    rewards: 25.5,
    tier: 'silver',
    features: ['30-day lock', '1.5% fee discount', 'Proposal creation', 'Early access'],
  },
  {
    id: 'gold',
    name: 'Gold Pool',
    description: 'Premium rewards for committed stakers',
    apy: 25,
    minStake: 5000,
    lockPeriod: 90,
    totalStaked: 12000000,
    yourStake: 0,
    rewards: 0,
    tier: 'gold',
    features: ['90-day lock', '2% fee discount', 'VIP features', 'Exclusive drops'],
  },
  {
    id: 'platinum',
    name: 'Platinum Pool',
    description: 'Maximum rewards for long-term holders',
    apy: 30,
    minStake: 25000,
    lockPeriod: 180,
    totalStaked: 35000000,
    yourStake: 0,
    rewards: 0,
    tier: 'platinum',
    features: ['180-day lock', '2.5% fee discount (0% fees)', 'Whitelist access', 'Revenue share'],
  },
];

const tierColors = {
  bronze: 'from-amber-700 to-amber-900',
  silver: 'from-gray-400 to-gray-600',
  gold: 'from-yellow-400 to-amber-600',
  platinum: 'from-slate-200 to-slate-400',
};

const tierBgColors = {
  bronze: 'bg-amber-500/10',
  silver: 'bg-gray-400/10',
  gold: 'bg-yellow-500/10',
  platinum: 'bg-slate-300/10',
};

const StakingPage: React.FC = () => {
  const [selectedPool, setSelectedPool] = useState<string | null>(null);
  const [stakeAmount, setStakeAmount] = useState('');
  const [activeTab, setActiveTab] = useState<'stake' | 'unstake'>('stake');
  const [showStakeModal, setShowStakeModal] = useState(false);

  // Mock user data
  const userData = {
    ndhBalance: 15000,
    totalStaked: 1500,
    totalRewards: 25.5,
    currentTier: 'silver' as const,
    pendingRewards: 12.3,
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const calculateEstimatedRewards = (amount: number, apy: number) => {
    return (amount * apy) / 100 / 12;
  };

  return (
    <div className="min-h-screen py-8">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-gradient">NDH Staking</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl">
            Stake your NDH tokens to earn rewards, reduce platform fees, and unlock exclusive features.
            Higher tiers provide better benefits.
          </p>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
        >
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-primary-500/10">
                <BanknotesIcon className="h-6 w-6 text-primary-400" />
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${tierBgColors[userData.currentTier]} text-white`}>
                {userData.currentTier} Tier
              </span>
            </div>
            <p className="text-gray-400 text-sm mb-1">NDH Balance</p>
            <p className="text-2xl font-bold">{formatNumber(userData.ndhBalance)} NDH</p>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-green-500/10">
                <LockClosedIcon className="h-6 w-6 text-green-400" />
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-1">Total Staked</p>
            <p className="text-2xl font-bold">{formatNumber(userData.totalStaked)} NDH</p>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-amber-500/10">
                <GiftIcon className="h-6 w-6 text-amber-400" />
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-1">Total Rewards Earned</p>
            <p className="text-2xl font-bold">{formatNumber(userData.totalRewards)} NDH</p>
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-xl bg-purple-500/10">
                <SparklesIcon className="h-6 w-6 text-purple-400" />
              </div>
              <button className="text-xs text-primary-400 hover:text-primary-300">Claim</button>
            </div>
            <p className="text-gray-400 text-sm mb-1">Pending Rewards</p>
            <p className="text-2xl font-bold">{formatNumber(userData.pendingRewards)} NDH</p>
          </div>
        </motion.div>

        {/* Tier Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold mb-6">Tier Benefits</h2>
          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Benefit</th>
                    {['Bronze', 'Silver', 'Gold', 'Platinum'].map((tier) => (
                      <th key={tier} className="px-6 py-4 text-center text-sm font-medium text-gray-400">
                        <span className={`px-3 py-1 rounded-full bg-gradient-to-r ${tierColors[tier.toLowerCase() as keyof typeof tierColors]} text-white`}>
                          {tier}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { benefit: 'APY', values: ['15%', '20%', '25%', '30%'] },
                    { benefit: 'Fee Discount', values: ['1%', '1.5%', '2%', '2.5% (0%)'] },
                    { benefit: 'Min Stake', values: ['100 NDH', '1K NDH', '5K NDH', '25K NDH'] },
                    { benefit: 'Lock Period', values: ['None', '30 days', '90 days', '180 days'] },
                    { benefit: 'Governance', values: ['Vote', 'Vote + Propose', 'Full Access', 'Full Access'] },
                    { benefit: 'Early Access', values: ['No', 'Yes', 'Yes', 'Priority'] },
                    { benefit: 'Revenue Share', values: ['No', 'No', 'No', 'Yes'] },
                  ].map((row, index) => (
                    <tr key={index} className="border-b border-white/5">
                      <td className="px-6 py-4 text-sm font-medium">{row.benefit}</td>
                      {row.values.map((value, i) => (
                        <td key={i} className="px-6 py-4 text-center text-sm text-gray-400">
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>

        {/* Staking Pools */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="text-2xl font-bold mb-6">Staking Pools</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {stakingPools.map((pool) => (
              <div
                key={pool.id}
                className={`glass-card overflow-hidden ${
                  pool.tier === userData.currentTier ? 'ring-2 ring-primary-500' : ''
                }`}
              >
                {/* Pool Header */}
                <div className={`p-6 bg-gradient-to-r ${tierColors[pool.tier]} bg-opacity-20`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold">{pool.name}</h3>
                      <p className="text-sm text-gray-300">{pool.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-white">{pool.apy}%</p>
                      <p className="text-sm text-gray-300">APY</p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-300">Pool Capacity</span>
                      <span className="text-white">{formatNumber(pool.totalStaked)} NDH</span>
                    </div>
                    <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-white rounded-full"
                        style={{ width: `${Math.min((pool.totalStaked / 50000000) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Pool Body */}
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Min Stake</p>
                      <p className="font-semibold">{formatNumber(pool.minStake)} NDH</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Lock Period</p>
                      <p className="font-semibold">
                        {pool.lockPeriod === 0 ? 'Flexible' : `${pool.lockPeriod} days`}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Your Stake</p>
                      <p className="font-semibold">{formatNumber(pool.yourStake)} NDH</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Your Rewards</p>
                      <p className="font-semibold text-green-400">+{pool.rewards} NDH</p>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mb-6">
                    <p className="text-sm text-gray-400 mb-2">Features</p>
                    <div className="flex flex-wrap gap-2">
                      {pool.features.map((feature, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-dark-700 rounded-full text-xs text-gray-300"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setSelectedPool(pool.id);
                        setActiveTab('stake');
                        setShowStakeModal(true);
                      }}
                      className="flex-1 btn-primary"
                    >
                      Stake
                    </button>
                    {pool.yourStake > 0 && (
                      <button
                        onClick={() => {
                          setSelectedPool(pool.id);
                          setActiveTab('unstake');
                          setShowStakeModal(true);
                        }}
                        className="flex-1 btn-secondary"
                      >
                        Unstake
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Staking Calculator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12"
        >
          <h2 className="text-2xl font-bold mb-6">Rewards Calculator</h2>
          <div className="glass-card p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Amount to Stake
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={stakeAmount}
                    onChange={(e) => setStakeAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="w-full px-4 py-3 pr-16 bg-dark-700 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500"
                  />
                  <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                    NDH
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Select Pool
                </label>
                <select
                  value={selectedPool || ''}
                  onChange={(e) => setSelectedPool(e.target.value)}
                  className="w-full px-4 py-3 bg-dark-700 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500"
                >
                  <option value="">Select a pool</option>
                  {stakingPools.map((pool) => (
                    <option key={pool.id} value={pool.id}>
                      {pool.name} ({pool.apy}% APY)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Estimated Monthly Rewards
                </label>
                <div className="px-4 py-3 bg-gradient-to-r from-primary-500/20 to-accent-500/20 border border-primary-500/30 rounded-xl">
                  <p className="text-2xl font-bold text-primary-400">
                    {selectedPool && stakeAmount
                      ? `+${calculateEstimatedRewards(
                          parseFloat(stakeAmount),
                          stakingPools.find((p) => p.id === selectedPool)?.apy || 0
                        ).toFixed(2)} NDH`
                      : '-- NDH'}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 rounded-xl bg-dark-700/50">
              <div className="flex items-start space-x-3">
                <InformationCircleIcon className="h-5 w-5 text-primary-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-400">
                  <p>
                    Rewards are calculated based on the current APY and distributed daily.
                    Actual rewards may vary based on pool performance and total staked amount.
                    Unstaking before the lock period ends may result in reduced rewards.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Buyback & Burn Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12"
        >
          <h2 className="text-2xl font-bold mb-6">Tokenomics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="glass-card p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
                <FireIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Buyback & Burn</h3>
              <p className="text-gray-400 text-sm mb-4">
                30% of platform fees are used to buy back and burn NDH tokens
              </p>
              <div className="p-4 rounded-xl bg-dark-700/50">
                <p className="text-sm text-gray-400">Total Burned</p>
                <p className="text-2xl font-bold text-orange-400">1,234,567 NDH</p>
              </div>
            </div>

            <div className="glass-card p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                <ChartBarIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Liquidity Mining</h3>
              <p className="text-gray-400 text-sm mb-4">
                Provide liquidity to earn additional NDH rewards
              </p>
              <div className="p-4 rounded-xl bg-dark-700/50">
                <p className="text-sm text-gray-400">LP APY</p>
                <p className="text-2xl font-bold text-green-400">45% APY</p>
              </div>
            </div>

            <div className="glass-card p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-500 to-violet-500 flex items-center justify-center">
                <ShieldCheckIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Curation Staking</h3>
              <p className="text-gray-400 text-sm mb-4">
                Stake on collections to earn curator rewards
              </p>
              <div className="p-4 rounded-xl bg-dark-700/50">
                <p className="text-sm text-gray-400">Active Curators</p>
                <p className="text-2xl font-bold text-purple-400">2,847</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default StakingPage;
